import { Controller } from '@hotwired/stimulus'
import { Chart, BarController, DoughnutController, BarElement, ArcElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js'

Chart.register(BarController, DoughnutController, BarElement, ArcElement, CategoryScale, LinearScale, Tooltip, Legend)

const ZINC_900 = '#18181B'
const ZINC_500 = '#71717A'
const ZINC_300 = '#D4D4D8'
const ZINC_200 = '#E4E4E7'

const METHOD_LABELS = {
  email_to_ghl: 'Email to GHL',
  phone_to_ghl: 'Phone to GHL',
  order_utm_campaign: 'Order UTM campaign',
  order_utm_meta: 'Order UTM meta',
  manual_link: 'Manual link',
  unattributed: 'Unattributed'
}

const CONFIDENCE_LABELS = {
  high: 'High confidence',
  medium: 'Medium confidence',
  low: 'Unattributed'
}

const CONFIDENCE_COLORS = {
  high: ZINC_900,
  medium: ZINC_500,
  low: ZINC_200
}

function formatDollars(cents) {
  if (!cents) return '$0'
  return '$' + Math.round(cents / 100).toLocaleString()
}

const tooltipConfig = {
  backgroundColor: '#FFFFFF',
  titleColor: ZINC_900,
  bodyColor: ZINC_500,
  borderColor: ZINC_200,
  borderWidth: 1,
  padding: 12,
  titleFont: { size: 11, weight: '600' },
  bodyFont: { size: 11, family: 'system-ui' },
  cornerRadius: 8,
  displayColors: true,
  boxWidth: 8,
  boxHeight: 8,
  boxPadding: 4,
  callbacks: {
    label(ctx) {
      return ' ' + formatDollars(ctx.raw)
    }
  }
}

export default class extends Controller {
  static values = { method: Object, confidence: Object }

  connect() {
    this._barChart = this._initBarChart()
    this._doughnutChart = this._initDoughnutChart()
  }

  disconnect() {
    this._barChart?.destroy()
    this._doughnutChart?.destroy()
  }

  _initBarChart() {
    const canvas = this.element.querySelector('[data-chart="method"]')
    if (!canvas) return null

    const data = this.methodValue
    const methods = ['email_to_ghl', 'phone_to_ghl', 'order_utm_campaign', 'order_utm_meta', 'manual_link', 'unattributed']
    const labels = methods.map(m => METHOD_LABELS[m] || m)
    const values = methods.map(m => data[m] || 0)
    const colors = methods.map(m => m === 'unattributed' ? ZINC_300 : ZINC_900)

    return new Chart(canvas, {
      type: 'bar',
      data: {
        labels,
        datasets: [{
          data: values,
          backgroundColor: colors,
          borderRadius: 4,
          barThickness: 20
        }]
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: tooltipConfig
        },
        scales: {
          x: {
            grid: { color: ZINC_200, lineWidth: 0.5 },
            border: { display: false },
            ticks: {
              font: { size: 11 },
              color: ZINC_500,
              callback(v) { return formatDollars(v) }
            }
          },
          y: {
            grid: { display: false },
            border: { display: false },
            ticks: {
              font: { size: 11 },
              color: ZINC_500
            }
          }
        }
      }
    })
  }

  _initDoughnutChart() {
    const canvas = this.element.querySelector('[data-chart="confidence"]')
    if (!canvas) return null

    const data = this.confidenceValue
    const segments = ['high', 'medium', 'low']
    const labels = segments.map(s => CONFIDENCE_LABELS[s] || s)
    const values = segments.map(s => data[s] || 0)
    const colors = segments.map(s => CONFIDENCE_COLORS[s] || ZINC_200)
    const total = values.reduce((a, b) => a + b, 0)
    const attributed = (data.high || 0) + (data.medium || 0)
    const matchRate = total > 0 ? Math.round((attributed / total) * 100) : 0

    return new Chart(canvas, {
      type: 'doughnut',
      data: {
        labels,
        datasets: [{
          data: values,
          backgroundColor: colors,
          borderWidth: 0,
          spacing: 2
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '70%',
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              color: ZINC_500,
              font: { size: 11 },
              usePointStyle: true,
              pointStyle: 'circle',
              padding: 16
            }
          },
          tooltip: {
            ...tooltipConfig,
            callbacks: {
              label(ctx) {
                const pct = total > 0 ? ((ctx.raw / total) * 100).toFixed(1) : '0.0'
                return ` ${formatDollars(ctx.raw)} (${pct}%)`
              }
            }
          }
        }
      },
      plugins: [{
        id: 'centerText',
        afterDraw(chart) {
          const { ctx, width, height } = chart
          const yCenter = (chart.chartArea.top + chart.chartArea.bottom) / 2
          ctx.save()
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillStyle = ZINC_900
          ctx.font = '600 28px system-ui'
          ctx.fillText(`${matchRate}%`, width / 2, yCenter - 8)
          ctx.fillStyle = ZINC_500
          ctx.font = '500 11px system-ui'
          ctx.fillText('match rate', width / 2, yCenter + 14)
          ctx.restore()
        }
      }]
    })
  }
}
